import { useImperativeHandle } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';

const escrowInvitationFormSchema = z.object({
  userName: z
    .string()
    .min(1, 'Enter a contact name.')
    .transform((val) => val.trim()),
  userEmail: z.string().min(1, 'Enter a contact email.').email('Invalid email format'),
  companyName: z
    .string()
    .min(1, 'Enter the organization name.')
    .transform((val) => val.trim()),
});

export type IEscrowInvitationForm = z.infer<typeof escrowInvitationFormSchema>;

export type IEscrowInvitationFormHandle = {
  submit: () => Promise<IEscrowInvitationForm | null>;
  focusCompanyName: () => void;
};

type Props = {
  ref?: React.Ref<IEscrowInvitationFormHandle>;
  onSubmit: (values: IEscrowInvitationForm) => Promise<void>;
  onCancel?: () => void;
  defaultValues?: IEscrowInvitationForm;
  hideActions?: boolean;
};

const EscrowInvitationForm = ({ ref, onSubmit, onCancel, defaultValues, hideActions = false }: Props) => {
  const form = useForm<IEscrowInvitationForm>({
    resolver: zodResolver(escrowInvitationFormSchema),
    defaultValues,
  });

  useImperativeHandle(ref, () => ({
    submit: () =>
      new Promise((resolve) => {
        void form.handleSubmit(
          (values) => resolve(values),
          () => resolve(null),
        )();
      }),
    focusCompanyName: () => form.setFocus('companyName'),
  }));

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} aria-label="party-invitation-form">
        <div className="space-y-4 pb-6">
          <FormField
            control={form.control}
            name="companyName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Organization name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid gap-4 sm:grid-cols-2 sm:gap-6">
            <FormField
              control={form.control}
              name="userName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Primary contact name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="userEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Primary contact email</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        {!hideActions && (
          <div className="flex justify-between gap-4">
            <Button variant="outline" type="button" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" isLoading={form.formState.isSubmitting} disabled={form.formState.isSubmitting}>
              Save
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
};

export default EscrowInvitationForm;
